import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  format, parse, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addDays, addWeeks, addMonths, subDays, subWeeks, subMonths,
  isSameMonth, isToday, startOfDay
} from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronRight, Plus, Clock, User, Phone,
  Calendar as CalendarIcon, Search, Download, RefreshCw,
  Check, X, MessageCircle, Eye, UserCog
} from 'lucide-react';

import { DurationSelect, SLOT_MINUTES, DEFAULT_DURATION } from '../components/DurationSelect';
import { AvailableTimeSelect } from '../components/AvailableTimeSelect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Appointment type configurations (Ophthalmology workflow)
const APPOINTMENT_TYPES = {
  primera_consulta: { name: 'Primera consulta', color: '#c2410c', borderColor: '#9a3412', duration: 30 },
  control: { name: 'Control', color: '#0d9488', borderColor: '#0f766e', duration: 15 },
  revision_rapida: { name: 'Revisión rápida', color: '#16a34a', borderColor: '#15803d', duration: 10 },
  cirugia: { name: 'Cirugía', color: '#db2777', borderColor: '#be185d', duration: 20 },
  post_operatorio: { name: 'Post operatorio', color: '#7c3aed', borderColor: '#6d28d9', duration: 10 },
  // Fallbacks for legacy data
  consulta: { name: 'Consulta', color: '#3B82F6', borderColor: '#2563EB', duration: 30 },
  post_op: { name: 'Post-Op', color: '#22C55E', borderColor: '#16A34A', duration: 15 },
  programacion_cirugia: { name: 'Prog. Cirugía', color: '#8B5CF6', borderColor: '#7C3AED', duration: 20 },
  revision_estudios: { name: 'Rev. Estudios', color: '#F97316', borderColor: '#EA580C', duration: 15 },
  emergencia: { name: 'Emergencia', color: '#dc2626', borderColor: '#b91c1c', duration: 10 },
};

// Status configurations
const STATUS_CONFIG = {
  agendada: { label: 'Agendada', dot: '#94A3B8' },
  pending: { label: 'Pendiente', dot: '#94A3B8' },
  confirmada: { label: 'Confirmada', dot: '#3B82F6' },
  confirmed: { label: 'Confirmada', dot: '#3B82F6' },
  en_consulta: { label: 'En consulta', dot: '#F59E0B' },
  atendida: { label: 'Atendida', dot: '#22C55E' },
  attended: { label: 'Atendida', dot: '#22C55E' },
  no_asistio: { label: 'No asistió', dot: '#EF4444' },
  no_show: { label: 'No asistió', dot: '#EF4444' },
  cancelada: { label: 'Cancelada', dot: '#D1D5DB' },
  cancelled: { label: 'Cancelada', dot: '#D1D5DB' },
};

const AgendaClinicaPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('daily'); // 'monthly', 'weekly', 'daily'
  const [appointments, setAppointments] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchingAppointments, setSearchingAppointments] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Google Calendar states
  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // New appointment form
  const [newAppointment, setNewAppointment] = useState({
    patient_name: '',
    patient_phone: '',
    appointment_date: '',
    appointment_time: '',
    appointment_type: 'primera_consulta',
    duration_minutes: DEFAULT_DURATION,
    professional_id: '',
    reason_text: '',
    internal_notes: '',
  });

  // Edit patient modal state
  const [showEditPatient, setShowEditPatient] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [savingPatient, setSavingPatient] = useState(false);
  
  // Patient autocomplete in edit modal
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedExistingPatientId, setSelectedExistingPatientId] = useState(null);
  const [currentAppointmentId, setCurrentAppointmentId] = useState(null);
  const [creatingNewPatient, setCreatingNewPatient] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch professionals
  const fetchProfessionals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data);
      // Set default professional
      const defaultProf = response.data.find(p => p.is_default) || response.data[0];
      if (defaultProf && !selectedProfessional) {
        setSelectedProfessional(defaultProf.id);
        setNewAppointment(prev => ({ ...prev, professional_id: defaultProf.id }));
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  }, [selectedProfessional]);

  // Fetch appointments for date range
  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      let startDate, endDate;
      
      if (view === 'monthly') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      } else if (view === 'weekly') {
        startDate = startOfWeek(currentDate, { locale: es });
        endDate = endOfWeek(currentDate, { locale: es });
      } else {
        startDate = startOfDay(currentDate);
        endDate = addDays(startDate, 1);
      }

      const response = await axios.get(`${API}/calendar/range`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        }
      });
      
      // API returns array directly, not {appointments: [...]}
      const appointmentsData = Array.isArray(response.data) ? response.data : (response.data.appointments || []);
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentDate, view]);

  // Fetch patients for autocomplete
  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, [token]);

  // Search appointments by patient name (past and future)
  const searchAppointments = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setSearchingAppointments(true);
    try {
      // Search in a wide range: 1 year back, 1 year forward
      const startDate = format(subMonths(new Date(), 12), 'yyyy-MM-dd');
      const endDate = format(addMonths(new Date(), 12), 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/calendar/range`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate }
      });
      
      // Filter by patient name
      const filtered = response.data
        .filter(apt => 
          apt.patient_name?.toLowerCase().includes(query.toLowerCase()) ||
          apt.patient_phone?.includes(query)
        )
        .sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date))
        .slice(0, 10); // Limit to 10 results
      
      setSearchResults(filtered);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching appointments:', error);
      setSearchResults([]);
    } finally {
      setSearchingAppointments(false);
    }
  }, [token]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchAppointments(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchAppointments]);

  // Navigate to a specific appointment date
  const goToAppointmentDate = (appointment) => {
    const appointmentDate = new Date(appointment.appointment_date + 'T12:00:00');
    setCurrentDate(appointmentDate);
    setView('daily');
    setSearchQuery('');
    setShowSearchResults(false);
    // Select the appointment after navigation
    setTimeout(() => {
      setSelectedAppointment(appointment);
    }, 500);
  };

  useEffect(() => {
    fetchProfessionals();
    fetchPatients();
  }, [fetchProfessionals, fetchPatients]);

  useEffect(() => {
    if (token) {
      fetchAppointments();
    }
  }, [fetchAppointments, token]);

  // Check Google Calendar connection
  const checkGoogleConnection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      checkGoogleConnection();
    }
  }, [token, checkGoogleConnection]);

  // Sync all appointments to Google Calendar
  const syncAllToGoogle = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar/google/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.synced} citas sincronizadas con Google Calendar`);
      fetchAppointments();
    } catch (error) {
      toast.error('Error al sincronizar con Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());
  
  const goToPrevious = () => {
    if (view === 'monthly') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'weekly') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };
  
  const goToNext = () => {
    if (view === 'monthly') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'weekly') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  // Filter appointments by professional
  const filteredAppointments = useMemo(() => {
    if (!selectedProfessional) return appointments;
    return appointments.filter(apt => 
      apt.professional_id === selectedProfessional || !apt.professional_id
    );
  }, [appointments, selectedProfessional]);

  // Handle slot click to create appointment
  const handleSlotClick = (date, time) => {
    setSelectedSlot({ date, time });
    setNewAppointment(prev => ({
      ...prev,
      appointment_date: format(date, 'yyyy-MM-dd'),
      appointment_time: time,
      professional_id: selectedProfessional || prev.professional_id,
    }));
    setShowNewAppointment(true);
  };

  // Handle appointment type change
  const handleTypeChange = (type) => {
    setNewAppointment(prev => ({ ...prev, appointment_type: type }));
  };

  // Create new appointment
  const handleCreateAppointment = async () => {
    try {
      const response = await axios.post(`${API}/appointments`, newAppointment, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cita creada exitosamente');
      setShowNewAppointment(false);
      setNewAppointment({
        patient_name: '',
        patient_phone: '',
        appointment_date: '',
        appointment_time: '',
        appointment_type: 'primera_consulta',
        duration_minutes: DEFAULT_DURATION,
        professional_id: selectedProfessional || '',
        reason_text: '',
        internal_notes: '',
      });
      fetchAppointments();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple error messages
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Error al crear la cita';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
        // Check for token expiration
        if (errorDetail === 'Token expired' || errorDetail === 'Invalid token') {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
        }
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation error - extract the first message
        errorMessage = errorDetail[0]?.msg || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  // Update appointment status
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Estado actualizado');
      fetchAppointments();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  // Get current professional
  const currentProfessional = useMemo(() => 
    professionals.find(p => p.id === selectedProfessional),
    [professionals, selectedProfessional]
  );

  // Open edit patient modal
  const handleOpenEditPatient = async (patientId, appointmentId = null, appointment = null) => {
    if (!patientId) {
      setEditingPatient({
        id: null,
        full_name: appointment?.patient_name || '',
        phone: appointment?.patient_phone || '',
        email: appointment?.patient_email || '',
        birth_date: '', sex: '', alternate_phone: '', referral_source: '', notes: ''
      });
      setPatientSearchQuery(appointment?.patient_name || '');
      setSelectedExistingPatientId(null);
      setCurrentAppointmentId(appointmentId);
      setShowEditPatient(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPatient({
        ...response.data,
        birth_date: response.data.birth_date || '',
        sex: response.data.sex || '',
        alternate_phone: response.data.alternate_phone || '',
        referral_source: response.data.referral_source || '',
        notes: response.data.notes || ''
      });
      setPatientSearchQuery(response.data.full_name || '');
      setSelectedExistingPatientId(patientId);
      setCurrentAppointmentId(appointmentId);
      setShowEditPatient(true);
    } catch (error) {
      toast.error('Error al cargar datos del paciente');
    }
  };

  // Search patients for autocomplete
  const searchPatientsForAutocomplete = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPatientSearchResults([]);
      return;
    }
    
    setSearchingPatients(true);
    try {
      const response = await axios.get(`${API}/patients/paginated`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: query, page_size: 8 }
      });
      setPatientSearchResults(response.data.items);
    } catch (error) {
      console.error('Error searching patients:', error);
      setPatientSearchResults([]);
    } finally {
      setSearchingPatients(false);
    }
  }, [token]);

  // Debounced patient search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (patientSearchQuery && showEditPatient) {
        searchPatientsForAutocomplete(patientSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearchQuery, showEditPatient, searchPatientsForAutocomplete]);

  // Select existing patient from autocomplete
  const handleSelectExistingPatient = (patient) => {
    setEditingPatient({
      id: patient.id,
      full_name: patient.full_name,
      phone: patient.phone || '',
      email: patient.email || '',
      birth_date: patient.birth_date || '',
      sex: patient.sex || '',
      alternate_phone: patient.alternate_phone || '',
      referral_source: patient.referral_source || '',
      notes: patient.notes || ''
    });
    setPatientSearchQuery(patient.full_name);
    setSelectedExistingPatientId(patient.id);
    setShowPatientDropdown(false);
    setPatientSearchResults([]);
  };

  // Create new patient from search query
  const handleCreateNewPatient = async () => {
    if (!patientSearchQuery.trim()) return;
    
    setCreatingNewPatient(true);
    try {
      const newPatientData = {
        full_name: patientSearchQuery.trim(),
        phone: editingPatient?.phone || '',
        email: editingPatient?.email || '',
        birth_date: editingPatient?.birth_date || null,
        sex: editingPatient?.sex || null,
        alternate_phone: editingPatient?.alternate_phone || '',
        referral_source: editingPatient?.referral_source || '',
        notes: editingPatient?.notes || ''
      };
      
      const response = await axios.post(`${API}/patients`, newPatientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdPatient = response.data;
      
      // Update form with new patient
      setEditingPatient({
        id: createdPatient.id,
        full_name: createdPatient.full_name,
        phone: createdPatient.phone || '',
        email: createdPatient.email || '',
        birth_date: createdPatient.birth_date || '',
        sex: createdPatient.sex || '',
        alternate_phone: createdPatient.alternate_phone || '',
        referral_source: createdPatient.referral_source || '',
        notes: createdPatient.notes || ''
      });
      setSelectedExistingPatientId(createdPatient.id);
      setShowPatientDropdown(false);
      setPatientSearchResults([]);
      
      toast.success(`Paciente "${createdPatient.full_name}" creado exitosamente`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el paciente');
    } finally {
      setCreatingNewPatient(false);
    }
  };

  // Check if patient name already exists (exact match)
  const patientNameExactMatch = patientSearchResults.some(p => 
    p.full_name.toLowerCase() === patientSearchQuery.toLowerCase().trim()
  );

  // Save edited patient
  const handleSavePatient = async (e) => {
    e.preventDefault();
    if (!editingPatient) return;
    if (!editingPatient.id) {
      toast.error('Seleccione un paciente existente de la lista o cree uno nuevo');
      return;
    }
    
    setSavingPatient(true);
    try {
      // Update patient data
      await axios.put(`${API}/patients/${editingPatient.id}`, editingPatient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If we selected a different patient, update the appointment's patient_id
      if (currentAppointmentId && selectedExistingPatientId) {
        await axios.put(`${API}/appointments/${currentAppointmentId}`, 
          { patient_id: selectedExistingPatientId },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }
      
      toast.success('Paciente actualizado');
      setShowEditPatient(false);
      setEditingPatient(null);
      setPatientSearchQuery('');
      setSelectedExistingPatientId(null);
      setCurrentAppointmentId(null);
      // Refresh appointments to show updated patient name
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar paciente');
    } finally {
      setSavingPatient(false);
    }
  };

  // Calculate age helper
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Brand */}
          <div className="flex flex-col">
            <h1 className="font-display text-2xl text-slate-900">Dr. Cristian R. Acevedo</h1>
            <p className="font-display text-sm text-slate-600">Médico y Cirujano · Oftalmólogo</p>
            <p className="font-mono-caps text-slate-400 mt-1">AGENDA CLÍNICA</p>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
              <Input
                placeholder="Buscar paciente o cita..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                className="pl-10 w-72"
                data-testid="search-appointments-input"
              />
              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {searchingAppointments ? (
                    <div className="p-3 text-center text-slate-500 text-sm">
                      Buscando...
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div>
                      {searchResults.map((apt) => {
                        const aptDate = new Date(apt.appointment_date + 'T12:00:00');
                        const isPast = aptDate < new Date();
                        return (
                          <button
                            key={apt.id}
                            onClick={() => goToAppointmentDate(apt)}
                            className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b last:border-b-0 flex items-center justify-between gap-4"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">
                                {apt.patient_name}
                              </p>
                              <p className="text-sm text-slate-500">
                                {format(aptDate, "EEEE d 'de' MMMM, yyyy", { locale: es })}
                                {apt.appointment_time && ` - ${apt.appointment_time}`}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge 
                                variant="outline" 
                                className={isPast ? 'text-slate-400' : 'text-blue-600'}
                              >
                                {isPast ? 'Pasada' : 'Próxima'}
                              </Badge>
                              <span className="text-xs text-slate-400">
                                {STATUS_CONFIG[apt.status]?.label || apt.status}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : searchQuery.length >= 2 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">
                      No se encontraron citas para "{searchQuery}"
                    </div>
                  ) : (
                    <div className="p-3 text-center text-slate-400 text-sm">
                      Escriba al menos 2 caracteres para buscar
                    </div>
                  )}
                </div>
              )}
            </div>
            {googleConnected && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={syncAllToGoogle}
                disabled={syncing}
                className="gap-2"
                data-testid="sync-google-button"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sync Google'}
              </Button>
            )}
            <Button variant="outline" size="icon">
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={fetchAppointments}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday}>
              Hoy
            </Button>
            <Button variant="ghost" size="icon" onClick={goToPrevious}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={goToNext}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <h2 className="font-display text-xl ml-2">
              {view === 'daily' && format(currentDate, "EEEE d 'de' MMMM, yyyy", { locale: es })}
              {view === 'weekly' && `${format(startOfWeek(currentDate, { locale: es }), "d MMM", { locale: es })} - ${format(endOfWeek(currentDate, { locale: es }), "d MMM yyyy", { locale: es })}`}
              {view === 'monthly' && format(currentDate, "MMMM yyyy", { locale: es })}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            {/* View Selector */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              {['monthly', 'weekly', 'daily'].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    view === v ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {v === 'monthly' ? 'Mes' : v === 'weekly' ? 'Semana' : 'Día'}
                </button>
              ))}
            </div>

            {/* New Appointment Button */}
            <Button onClick={() => { setNewAppointment(prev => ({ ...prev, appointment_date: prev.appointment_date || format(currentDate, 'yyyy-MM-dd') })); setShowNewAppointment(true); }} data-testid="new-appointment-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" />
              Nueva cita
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Calendar Area - Full width with scroll */}
        <div className="flex-1 bg-white" style={{ height: 'calc(100vh - 180px)', overflow: 'auto' }}>
          {view === 'daily' && (
            <DailyView
              date={currentDate}
              appointments={filteredAppointments}
              onSlotClick={handleSlotClick}
              onAppointmentClick={setSelectedAppointment}
              onStatusChange={handleStatusChange}
              currentTime={currentTime}
              professional={currentProfessional}
            />
          )}
          {view === 'weekly' && (
            <WeeklyView
              date={currentDate}
              appointments={filteredAppointments}
              onSlotClick={handleSlotClick}
              onAppointmentClick={setSelectedAppointment}
              onDayClick={(day) => { setCurrentDate(day); setView('daily'); }}
              currentTime={currentTime}
            />
          )}
          {view === 'monthly' && (
            <MonthlyView
              date={currentDate}
              appointments={filteredAppointments}
              onDayClick={(date) => { setCurrentDate(date); setView('daily'); }}
              onAppointmentClick={setSelectedAppointment}
            />
          )}
        </div>
      </div>

      {/* New Appointment Modal */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva cita</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Patient */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre del paciente</Label>
                <Input
                  value={newAppointment.patient_name}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, patient_name: e.target.value }))}
                  placeholder="Nombre completo"
                  list="patients-list"
                />
                <datalist id="patients-list">
                  {patients.map(p => (
                    <option key={p.id} value={p.full_name} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={newAppointment.patient_phone}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, patient_phone: e.target.value }))}
                  placeholder="+502 XXXX-XXXX"
                />
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={newAppointment.appointment_date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, appointment_date: e.target.value, appointment_time: '' }))}
                  data-testid="new-apt-date-input"
                />
              </div>
              <div>
                <Label>Hora</Label>
                <AvailableTimeSelect
                  date={newAppointment.appointment_date}
                  value={newAppointment.appointment_time}
                  onChange={(v) => setNewAppointment(prev => ({ ...prev, appointment_time: v }))}
                  testId="new-apt-time"
                />
              </div>
            </div>

            {/* Type & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de cita</Label>
                <Select 
                  value={newAppointment.appointment_type} 
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { key: 'primera_consulta', name: 'Primera consulta', color: '#c2410c' },
                      { key: 'control', name: 'Control', color: '#0d9488' },
                      { key: 'post_operatorio', name: 'Post operatorio', color: '#7c3aed' },
                      { key: 'emergencia', name: 'Emergencia', color: '#dc2626' },
                    ].map((type) => (
                      <SelectItem key={type.key} value={type.key} data-testid={`new-apt-type-${type.key}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded" style={{ backgroundColor: type.color }} />
                          {type.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duración</Label>
                <DurationSelect
                  value={newAppointment.duration_minutes}
                  onChange={(v) => setNewAppointment(prev => ({ ...prev, duration_minutes: v }))}
                  testId="new-apt-duration"
                />
              </div>
            </div>

            {/* Professional */}
            <div>
              <Label>Profesional</Label>
              <Select 
                value={newAppointment.professional_id} 
                onValueChange={(v) => setNewAppointment(prev => ({ ...prev, professional_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar profesional" />
                </SelectTrigger>
                <SelectContent>
                  {professionals.map((prof) => (
                    <SelectItem key={prof.id} value={prof.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: prof.color }} />
                        {prof.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Motivo / Notas</Label>
              <Textarea
                value={newAppointment.reason_text}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, reason_text: e.target.value }))}
                placeholder="Motivo de la consulta..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAppointment(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAppointment} disabled={!newAppointment.appointment_date || !newAppointment.appointment_time} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-appointment-submit">
              Crear cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onStatusChange={handleStatusChange}
          professionals={professionals}
          onUpdate={fetchAppointments}
          token={token}
          navigate={navigate}
          onEditPatient={handleOpenEditPatient}
        />
      )}

      {/* Edit Patient Modal */}
      <Dialog open={showEditPatient} onOpenChange={(open) => {
        setShowEditPatient(open);
        if (!open) {
          setPatientSearchQuery('');
          setPatientSearchResults([]);
          setSelectedExistingPatientId(null);
          setCurrentAppointmentId(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Editar Paciente</DialogTitle>
          </DialogHeader>
          {editingPatient && (
            <form onSubmit={handleSavePatient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Patient Name with Autocomplete */}
                <div className="col-span-2 relative">
                  <Label>Nombre Completo *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      value={patientSearchQuery}
                      onChange={(e) => {
                        setPatientSearchQuery(e.target.value);
                        setEditingPatient(prev => ({ ...prev, full_name: e.target.value }));
                        setShowPatientDropdown(true);
                      }}
                      onFocus={() => patientSearchQuery.length >= 2 && setShowPatientDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPatientDropdown(false), 200)}
                      required
                      placeholder="Buscar paciente existente o escribir nombre..."
                      className="pl-10"
                      data-testid="edit-patient-name"
                    />
                  </div>
                  
                  {/* Patient Autocomplete Dropdown */}
                  {showPatientDropdown && patientSearchQuery.length >= 2 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {searchingPatients ? (
                        <div className="px-3 py-2 text-sm text-slate-500 text-center">
                          Buscando...
                        </div>
                      ) : patientSearchResults.length > 0 ? (
                        <div>
                          <div className="px-3 py-1.5 text-xs text-slate-400 bg-slate-50 border-b">
                            Pacientes existentes
                          </div>
                          {patientSearchResults.map(patient => (
                            <button
                              key={patient.id}
                              type="button"
                              onClick={() => handleSelectExistingPatient(patient)}
                              className={`w-full px-3 py-2.5 text-left hover:bg-blue-50 flex items-center justify-between border-b last:border-b-0 ${
                                selectedExistingPatientId === patient.id ? 'bg-blue-50' : ''
                              }`}
                              data-testid={`patient-option-${patient.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                  <User className="h-4 w-4 text-slate-500" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">{patient.full_name}</p>
                                  <p className="text-xs text-slate-500">{patient.phone || 'Sin teléfono'}</p>
                                </div>
                              </div>
                              {selectedExistingPatientId === patient.id && (
                                <Check className="h-4 w-4 text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-sm text-slate-500">
                          No se encontraron pacientes con "{patientSearchQuery}"
                        </div>
                      )}
                      
                      {/* Create New Patient Option - show when no exact match exists */}
                      {patientSearchQuery.trim() && !patientNameExactMatch && !searchingPatients && (
                        <button
                          type="button"
                          onClick={handleCreateNewPatient}
                          disabled={creatingNewPatient}
                          className="w-full px-3 py-2.5 text-left bg-green-50 hover:bg-green-100 border-t flex items-center gap-3 text-green-700"
                          data-testid="create-new-patient-button"
                        >
                          <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">
                            <Plus className="h-4 w-4 text-green-700" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {creatingNewPatient ? 'Creando paciente...' : `Crear nuevo paciente`}
                            </p>
                            <p className="text-xs text-green-600">"{patientSearchQuery.trim()}"</p>
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                  
                  {selectedExistingPatientId && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Paciente vinculado al expediente existente
                    </p>
                  )}
                </div>
                <div>
                  <Label>Teléfono Principal *</Label>
                  <Input
                    value={editingPatient.phone || ''}
                    onChange={(e) => setEditingPatient(prev => ({ ...prev, phone: e.target.value }))}
                    required
                    placeholder="Ej: 5555-1234"
                    data-testid="edit-patient-phone"
                  />
                </div>
                <div>
                  <Label>Teléfono Alternativo</Label>
                  <Input
                    value={editingPatient.alternate_phone || ''}
                    onChange={(e) => setEditingPatient(prev => ({ ...prev, alternate_phone: e.target.value }))}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingPatient.email || ''}
                    onChange={(e) => setEditingPatient(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div>
                  <Label>Fecha de Nacimiento</Label>
                  <Input
                    type="date"
                    value={editingPatient.birth_date || ''}
                    onChange={(e) => setEditingPatient(prev => ({ ...prev, birth_date: e.target.value }))}
                  />
                  {editingPatient.birth_date && (
                    <p className="text-xs text-slate-500 mt-1">
                      Edad: {calculateAge(editingPatient.birth_date)} años
                    </p>
                  )}
                </div>
                <div>
                  <Label>Sexo</Label>
                  <Select 
                    value={editingPatient.sex || ''} 
                    onValueChange={(v) => setEditingPatient(prev => ({ ...prev, sex: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>¿Cómo nos conoció?</Label>
                  <Select 
                    value={editingPatient.referral_source || ''} 
                    onValueChange={(v) => setEditingPatient(prev => ({ ...prev, referral_source: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recomendacion">Recomendación</SelectItem>
                      <SelectItem value="redes_sociales">Redes Sociales</SelectItem>
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="publicidad">Publicidad</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Notas</Label>
                  <Textarea
                    value={editingPatient.notes || ''}
                    onChange={(e) => setEditingPatient(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Notas adicionales sobre el paciente..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-4 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditPatient(false);
                    setEditingPatient(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#0891B2] hover:bg-[#0e7490]"
                  disabled={savingPatient}
                  data-testid="save-patient-button"
                >
                  {savingPatient ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Daily View Component - Premium Medical Design
const findScroller = (el) => {
  let node = el?.parentElement;
  while (node && !(/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight)) node = node.parentElement;
  return node;
};

const DailyView = ({ date, appointments, onSlotClick, onAppointmentClick, onStatusChange, currentTime, professional }) => {
  const startHour = 7;
  const endHour = 18;
  const slotHeight = 24; // px per 10-min slot
  const slotsPerHour = 60 / SLOT_MINUTES;
  
  const totalSlots = (endHour - startHour) * slotsPerHour;
  const timeSlots = Array.from({ length: totalSlots }, (_, i) => {
    const totalMinutes = startHour * 60 + i * SLOT_MINUTES;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  });

  const dateStr = format(date, 'yyyy-MM-dd');
  const dayAppointments = appointments.filter(apt => apt.appointment_date === dateStr);

  // Calculate current time line position
  const currentTimePosition = useMemo(() => {
    if (!isToday(date)) return null;
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();
    if (hours < startHour || hours >= endHour) return null;
    const totalMinutes = (hours - startHour) * 60 + minutes;
    return (totalMinutes / SLOT_MINUTES) * slotHeight;
  }, [currentTime, date, slotHeight, startHour, endHour]);

  // Keep the current time in view: scroll on open and follow it every minute,
  // pausing for 2 minutes after the user scrolls manually
  const nowLineRef = useRef(null);
  const lastUserScrollRef = useRef(0);
  const programmaticUntilRef = useRef(0);
  const hasNowLine = currentTimePosition !== null;
  useEffect(() => {
    const scroller = findScroller(nowLineRef.current);
    if (!scroller) return undefined;
    const onScroll = () => {
      if (Date.now() < programmaticUntilRef.current) return;
      lastUserScrollRef.current = Date.now();
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [dateStr, hasNowLine]);

  useEffect(() => {
    const line = nowLineRef.current;
    const scroller = findScroller(line);
    if (!scroller || Date.now() - lastUserScrollRef.current < 120000) return;
    const offset = line.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    const target = Math.max(0, scroller.scrollTop + offset - scroller.clientHeight / 3);
    if (Math.abs(target - scroller.scrollTop) < 4) return;
    programmaticUntilRef.current = Date.now() + 1500;
    scroller.scrollTo({ top: target, behavior: 'smooth' });
  }, [currentTimePosition, dateStr]);

  // Group overlapping appointments
  const getAppointmentLayout = () => {
    const sortedApts = [...dayAppointments].sort((a, b) => 
      a.appointment_time.localeCompare(b.appointment_time)
    );
    
    const positions = new Map();
    const processed = new Set();
    
    sortedApts.forEach((apt, index) => {
      if (processed.has(apt.id)) return;
      
      const [h, m] = apt.appointment_time.split(':').map(Number);
      const aptStart = h * 60 + m;
      const aptEnd = aptStart + (apt.duration_minutes || 10);
      
      let group = [apt];
      let groupEnd = aptEnd;
      
      for (let i = index + 1; i < sortedApts.length; i++) {
        const nextApt = sortedApts[i];
        const [nh, nm] = nextApt.appointment_time.split(':').map(Number);
        const nextStart = nh * 60 + nm;
        
        if (nextStart < groupEnd) {
          group.push(nextApt);
          const nextEnd = nextStart + (nextApt.duration_minutes || 10);
          groupEnd = Math.max(groupEnd, nextEnd);
        }
      }
      
      group.forEach((groupApt, groupIndex) => {
        positions.set(groupApt.id, {
          column: groupIndex,
          totalColumns: Math.min(group.length, 3)
        });
        processed.add(groupApt.id);
      });
    });
    
    return positions;
  };

  const aptPositions = getAppointmentLayout();

  return (
    <div className="h-full overflow-auto bg-slate-50/50">
      <div className="flex min-h-max p-4">
        {/* Time Labels */}
        <div className="w-20 flex-shrink-0 pr-4">
          {timeSlots.map((time, i) => (
            i % slotsPerHour === 0 && (
              <div
                key={time}
                className="text-sm font-medium text-slate-500"
                style={{ height: slotHeight * slotsPerHour }}
              >
                {time}
              </div>
            )
          ))}
        </div>

        {/* Appointments Grid */}
        <div className="flex-1 relative">
          {/* Hour Lines */}
          {timeSlots.map((time, i) => (
            <div
              key={time}
              onClick={() => onSlotClick(date, time)}
              className={`cursor-pointer transition-colors ${
                i % slotsPerHour === 0 
                  ? 'border-t border-slate-200' 
                  : 'border-t border-slate-100/50'
              } hover:bg-blue-50/30`}
              style={{ height: slotHeight }}
            />
          ))}

          {/* Current Time Line */}
          {currentTimePosition !== null && (
            <div
              ref={nowLineRef}
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ top: currentTimePosition }}
              data-testid="current-time-line"
            >
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 shadow-sm" />
                <div className="flex-1 h-0.5 bg-red-500/70" />
              </div>
            </div>
          )}

          {/* Appointment Cards */}
          {dayAppointments.map((apt) => {
            const [hours, minutes] = apt.appointment_time.split(':').map(Number);
            const topMinutes = (hours - startHour) * 60 + minutes;
            const top = (topMinutes / SLOT_MINUTES) * slotHeight;
            const cardHeight = ((apt.duration_minutes || DEFAULT_DURATION) / SLOT_MINUTES) * slotHeight - 2;
            
            const typeConfig = APPOINTMENT_TYPES[apt.appointment_type] || APPOINTMENT_TYPES.primera_consulta;
            const statusConfig = STATUS_CONFIG[apt.status] || STATUS_CONFIG.pending;
            const isCancelled = apt.status === 'cancelada' || apt.status === 'cancelled';
            
            const position = aptPositions.get(apt.id) || { column: 0, totalColumns: 1 };
            const cardWidth = `calc(${100 / position.totalColumns}% - 12px)`;
            const cardLeft = `calc(${(position.column / position.totalColumns) * 100}% + 4px)`;

            return (
              <div
                key={apt.id}
                onClick={(e) => { e.stopPropagation(); onAppointmentClick(apt); }}
                className={`absolute bg-white rounded shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${
                  isCancelled ? 'opacity-50' : ''
                }`}
                style={{
                  top: top + 1,
                  height: cardHeight,
                  width: cardWidth,
                  left: cardLeft,
                  borderLeft: `4px solid ${typeConfig.color}`,
                }}
                data-testid={`appointment-card-${apt.id}`}
              >
                <div className="px-2 h-full flex items-center justify-between">
                  {/* Time + Patient Name */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-slate-600 flex-shrink-0">
                      {apt.appointment_time}
                    </span>
                    <span className="font-semibold text-slate-800 text-xs truncate">
                      {apt.patient_name}
                    </span>
                  </div>
                  
                  {/* Status + Type */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: statusConfig.dot }}
                      title={statusConfig.label}
                    />
                    <span className="text-[10px] text-slate-400 font-medium hidden lg:inline">
                      {typeConfig.name}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Weekly View Component - Premium Medical Design
const WeeklyView = ({ date, appointments, onSlotClick, onAppointmentClick, onDayClick, currentTime }) => {
  const startHour = 7;
  const endHour = 18;
  const slotHeight = 28; // px per 10-min slot
  const slotsPerHour = 60 / SLOT_MINUTES;
  const hours = Array.from({ length: (endHour - startHour) * slotsPerHour }, (_, i) => {
    const totalMinutes = startHour * 60 + i * SLOT_MINUTES;
    return {
      time: `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`,
      isHour: i % slotsPerHour === 0
    };
  });
  
  const weekStart = startOfWeek(date, { locale: es });
  const days = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="h-full overflow-auto bg-slate-50/50">
      <div className="flex min-w-[1000px]">
        {/* Time Labels */}
        <div className="w-20 flex-shrink-0 sticky left-0 bg-slate-50/50 z-20">
          {/* Empty space for day headers */}
          <div className="h-14"></div>
          {hours.map(({ time, isHour }) => (
            isHour && (
              <div
                key={time}
                className="text-sm font-medium text-slate-500 pr-4"
                style={{ height: slotHeight * slotsPerHour }}
              >
                {time}
              </div>
            )
          ))}
        </div>

        {/* Days Grid */}
        <div className="flex-1 grid grid-cols-6 gap-2 p-2">
          {days.map((day) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayAppointments = appointments.filter(apt => apt.appointment_date === dayStr);
            const dayIsToday = isToday(day);

            return (
              <div key={dayStr} className="relative bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Day Header */}
                <div 
                  onClick={() => onDayClick && onDayClick(day)}
                  className={`h-14 flex flex-col items-center justify-center border-b cursor-pointer transition-colors sticky top-0 z-10 ${
                    dayIsToday 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <span className={`text-xs uppercase font-medium ${dayIsToday ? 'text-blue-100' : 'text-slate-400'}`}>
                    {format(day, 'EEE', { locale: es })}
                  </span>
                  <span className={`text-lg font-semibold ${dayIsToday ? 'text-white' : 'text-slate-700'}`}>
                    {format(day, 'd')}
                  </span>
                </div>

                {/* Time Slots with Appointments */}
                <div className="relative">
                  {hours.map(({ time, isHour }, slotIndex) => {
                    const [slotHour, slotMin] = time.split(':').map(Number);
                    const slotStart = slotHour * 60 + slotMin;
                    const slotEnd = slotStart + SLOT_MINUTES;
                    const longest = Math.max(SLOT_MINUTES, ...dayAppointments
                      .filter(apt => { const [h, m] = apt.appointment_time.split(':').map(Number); const st = h * 60 + m; return st >= slotStart && st < slotEnd; })
                      .map(apt => apt.duration_minutes || DEFAULT_DURATION));
                    
                    const slotAppointments = dayAppointments.filter(apt => {
                      const [aptHour, aptMin] = apt.appointment_time.split(':').map(Number);
                      const aptStart = aptHour * 60 + aptMin;
                      return aptStart >= slotStart && aptStart < slotEnd;
                    });
                    
                    return (
                      <div
                        key={time}
                        onClick={() => onSlotClick(day, time)}
                        className={`cursor-pointer transition-colors hover:bg-blue-50/30 relative ${
                          isHour ? 'border-t border-slate-200' : 'border-t border-slate-100/50'
                        }`}
                        style={{ height: slotHeight, minHeight: slotHeight }}
                      >
                        {/* Appointments in this slot */}
                        {slotAppointments.length > 0 && (
                          <div className="absolute inset-x-1 top-1 flex gap-1 z-10" style={{ height: (longest / SLOT_MINUTES) * slotHeight - 8 }}>
                            {slotAppointments.map((apt) => {
                              const typeConfig = APPOINTMENT_TYPES[apt.appointment_type] || APPOINTMENT_TYPES.primera_consulta;
                              const statusConfig = STATUS_CONFIG[apt.status] || STATUS_CONFIG.pending;
                              const isCancelled = apt.status === 'cancelada' || apt.status === 'cancelled';
                              
                              return (
                                <div
                                  key={apt.id}
                                  onClick={(e) => { e.stopPropagation(); onAppointmentClick(apt); }}
                                  className={`flex-1 bg-white rounded shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center px-1 overflow-hidden ${
                                    isCancelled ? 'opacity-40' : ''
                                  }`}
                                  style={{
                                    borderLeft: `3px solid ${typeConfig.color}`,
                                    maxWidth: slotAppointments.length > 1 ? '50%' : '100%'
                                  }}
                                >
                                  <span className="text-[9px] font-bold text-slate-500 mr-1 flex-shrink-0">
                                    {apt.appointment_time}
                                  </span>
                                  <span className="font-medium text-slate-700 text-[9px] truncate flex-1">
                                    {apt.patient_name}
                                  </span>
                                  <div 
                                    className="w-1.5 h-1.5 rounded-full ml-1 flex-shrink-0"
                                    style={{ backgroundColor: statusConfig.dot }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Monthly View Component
const MonthlyView = ({ date, appointments, onDayClick, onAppointmentClick }) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calendarStart = startOfWeek(monthStart, { locale: es });
  const calendarEnd = endOfWeek(monthEnd, { locale: es });

  const days = [];
  let day = calendarStart;
  while (day <= calendarEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="p-4">
      {/* Day Headers */}
      <div className="grid grid-cols-7 mb-2">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
          <div key={d} className="text-center text-xs font-medium text-slate-500 uppercase py-2">
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="space-y-1">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-1">
            {week.map((day) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const dayAppointments = appointments.filter(apt => apt.appointment_date === dayStr);
              const isCurrentMonth = isSameMonth(day, date);
              const dayIsToday = isToday(day);

              return (
                <div
                  key={dayStr}
                  onClick={() => onDayClick(day)}
                  className={`min-h-[130px] p-1 rounded-lg border cursor-pointer transition-colors ${
                    !isCurrentMonth ? 'bg-slate-50 text-slate-400' : 'bg-white hover:bg-slate-50'
                  } ${dayIsToday ? 'border-blue-500 border-2' : 'border-slate-200'}`}
                >
                  <div className={`text-sm font-medium mb-1 ${dayIsToday ? 'text-blue-600' : ''}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayAppointments.slice(0, 3).map((apt) => {
                      const typeConfig = APPOINTMENT_TYPES[apt.appointment_type] || APPOINTMENT_TYPES.primera_consulta;
                      return (
                        <div
                          key={apt.id}
                          onClick={(e) => { e.stopPropagation(); onAppointmentClick(apt); }}
                          className="text-[10px] px-1.5 py-1 rounded truncate font-medium bg-white border-l-2 shadow-sm hover:shadow"
                          style={{ borderLeftColor: typeConfig.color }}
                        >
                          <span className="text-slate-500">{apt.appointment_time}</span>
                          <span className="text-slate-800 ml-1">{apt.patient_name}</span>
                        </div>
                      );
                    })}
                    {dayAppointments.length > 3 && (
                      <div className="text-[10px] text-slate-500 px-1">
                        +{dayAppointments.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// Appointment Detail Modal
const AppointmentDetailModal = ({ appointment, onClose, onStatusChange, professionals, onUpdate, token, navigate, onEditPatient }) => {
  const typeConfig = APPOINTMENT_TYPES[appointment.appointment_type] || APPOINTMENT_TYPES.primera_consulta;
  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.agendada;
  const professional = professionals.find(p => p.id === appointment.professional_id);

  const handleDurationChange = async (duration) => {
    try {
      await axios.put(`${API}/appointments/${appointment.id}`, { duration_minutes: duration }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      appointment.duration_minutes = duration;
      toast.success(`Duración actualizada a ${duration} min`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar la duración');
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-3 h-12 rounded" style={{ backgroundColor: typeConfig.color }} />
            <div>
              <DialogTitle className="font-display text-xl">{appointment.patient_name}</DialogTitle>
              <p className="text-sm text-slate-500">{typeConfig.name}</p>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-slate-400" />
              <span>{format(parse(appointment.appointment_date, 'yyyy-MM-dd', new Date()), "d 'de' MMMM, yyyy", { locale: es })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <span>{appointment.appointment_time}</span>
              <DurationSelect
                value={appointment.duration_minutes || DEFAULT_DURATION}
                onChange={handleDurationChange}
                testId="apt-detail-duration"
                className="h-8 w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              <span>{appointment.patient_phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <span>{professional?.name || 'Sin asignar'}</span>
            </div>
          </div>

          {/* Status */}
          <div>
            <Label className="text-xs text-slate-500">Estado</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { key: 'agendada', label: 'Agendada' },
                { key: 'confirmada', label: 'Confirmada' },
                { key: 'en_consulta', label: 'En consulta' },
                { key: 'atendida', label: 'Atendida' },
                { key: 'no_asistio', label: 'No asistió' },
                { key: 'cancelada', label: 'Cancelada' },
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant={appointment.status === key ? 'default' : 'outline'}
                  size="sm"
                  className={appointment.status === key ? 'bg-blue-600' : ''}
                  onClick={() => onStatusChange(appointment.id, key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Notes */}
          {appointment.reason_text && (
            <div>
              <Label className="text-xs text-slate-500">Motivo</Label>
              <p className="text-sm mt-1">{appointment.reason_text}</p>
            </div>
          )}
          {appointment.internal_notes && (
            <div>
              <Label className="text-xs text-slate-500">Notas internas</Label>
              <p className="text-sm mt-1">{appointment.internal_notes}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2 flex-wrap">
          <Button
            variant="outline"
            className={appointment.patient_id ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100" : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"}
            onClick={() => {
              onClose();
              onEditPatient(appointment.patient_id, appointment.id, appointment);
            }}
            data-testid="edit-patient-button"
            title={appointment.patient_id ? 'Editar datos del paciente' : 'Vincular la cita a un expediente existente o crear uno nuevo'}
          >
            <UserCog className="h-4 w-4 mr-2" />
            {appointment.patient_id ? 'Editar Paciente' : 'Asignar Paciente'}
          </Button>
          {appointment.patient_id && (
            <>
              <Button 
                variant="outline" 
                className="bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100"
                onClick={() => {
                  onClose();
                  navigate(`/admin/patients/${appointment.patient_id}/evaluacion-kanban`);
                }}
                data-testid="evaluation-button"
              >
                <Eye className="h-4 w-4 mr-2" />
                Evaluación
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            onClick={() => {
              const appointmentDate = new Date(appointment.appointment_date + 'T12:00:00');
              const formattedDate = format(appointmentDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
              const message = `¡Hola ${appointment.patient_name}! 👋

Le recordamos su cita con el *Dr. Cristian Acevedo - Oftalmólogo*:

📅 *Fecha:* ${formattedDate}
🕐 *Hora:* ${appointment.appointment_time}
${appointment.reason_text ? `📋 *Motivo:* ${appointment.reason_text}` : ''}

Por favor confirme su asistencia respondiendo a este mensaje.

¡Gracias por su preferencia!`;
              const cleanPhone = appointment.patient_phone?.replace(/[^0-9]/g, '') || '';
              const phoneWithCountry = cleanPhone.startsWith('502') ? cleanPhone : `502${cleanPhone}`;
              window.open(`https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`, '_blank');
            }}
            data-testid="whatsapp-reminder-modal-button"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgendaClinicaPage;
